/**
 * Does a trip collide with the traveller's other commitments? (ADR 0030, plan §5.7, EC-2, EC-3.)
 * Pure and RN-safe: plain rows in, a decision out; the API loads the rows.
 *
 * Overlap is a WARNING on a trip, never a refusal — a person may take leave in the middle of a
 * trip, or have a second trip booked back to back. The one refusal is a duplicate: the same person
 * going to the same place over the same days, however the journey is shaped (`isDuplicateTrip`).
 *
 * Part-day rules, the same as leave's own overlap check (`apps/api/src/lib/leave-overlap.ts`, which
 * this package cannot import):
 *
 *  - A day is `full`, `first_half` or `second_half`. Two halves conflict only when they are the
 *    same half; `full` conflicts with anything.
 *  - A trip that DEPARTS at or after midday takes only the second half of its first day; one that
 *    RETURNS at or before midday takes only the first half of its last day. Without times, both
 *    days are full.
 *  - A single-day leave request's two sessions are read together; on a multi-day request the
 *    start session applies to the first day and the end session to the last.
 *  - An HOURLY leave request says how long, never when, so it takes its whole date.
 *  - On-duty rows are whole days.
 *
 * The caller passes only rows that hold their dates (pending/approved leave and on-duty, open
 * trips) and leaves out the trip's own rows (its on-duty days, itself).
 */

import type { TravelTripStatus } from '../enums.js';
import { clockMinutes, eachDay } from './dates.js';

export type DaySession = 'full' | 'first_half' | 'second_half';

/** Where a day splits into halves for a trip's departure and return times. */
export const TRAVEL_MIDDAY = '13:00';

/** A trip's span. Times are local `HH:MM` on the first and last day. */
export interface TripSpan {
  startDate: string;
  endDate: string;
  departureTime?: string | null;
  returnTime?: string | null;
}

export interface TripCommitment extends TripSpan {
  id: string;
}

/** A leave request row, as `leave-overlap.ts` reads it. Sessions are plain strings on stored rows. */
export interface LeaveCommitment {
  id: string;
  startDate: string;
  endDate: string;
  startSession: string;
  endSession: string;
  /** Set only for an hourly request. */
  hours?: string | null;
}

export interface OnDutyCommitment {
  id: string;
  startDate: string;
  endDate: string;
}

export interface TripCommitments {
  leave?: readonly LeaveCommitment[];
  trips?: readonly TripCommitment[];
  onDuty?: readonly OnDutyCommitment[];
}

export type TripOverlapKind = 'leave' | 'trip' | 'on_duty';

export interface TripOverlap {
  kind: TripOverlapKind;
  id: string;
  /** The shared days, ascending. */
  dates: string[];
}

// ---------------------------------------------------------------------------
// Which part of a day each row takes
// ---------------------------------------------------------------------------

interface DayCover {
  startDate: string;
  endDate: string;
  sessionOn(date: string): DaySession;
}

function asSession(value: string): DaySession {
  return value === 'first_half' || value === 'second_half' ? value : 'full';
}

function tripCover(trip: TripSpan): DayCover {
  const midday = clockMinutes(TRAVEL_MIDDAY)!;
  const departure = clockMinutes(trip.departureTime);
  const ret = clockMinutes(trip.returnTime);
  const leavesAfternoon = departure !== null && departure >= midday;
  const backByMidday = ret !== null && ret <= midday;
  return {
    startDate: trip.startDate,
    endDate: trip.endDate,
    sessionOn(date) {
      if (date === trip.startDate && leavesAfternoon) return 'second_half';
      if (date === trip.endDate && backByMidday) return 'first_half';
      return 'full';
    },
  };
}

function leaveCover(leave: LeaveCommitment): DayCover {
  const hourly = leave.hours !== undefined && leave.hours !== null && leave.hours !== '';
  return {
    startDate: leave.startDate,
    endDate: leave.endDate,
    sessionOn(date) {
      if (hourly) return 'full';
      if (leave.startDate === leave.endDate) {
        if (leave.startSession !== 'full') return asSession(leave.startSession);
        return asSession(leave.endSession);
      }
      if (date === leave.startDate) return asSession(leave.startSession);
      if (date === leave.endDate) return asSession(leave.endSession);
      return 'full';
    },
  };
}

function wholeDays(row: { startDate: string; endDate: string }): DayCover {
  return { startDate: row.startDate, endDate: row.endDate, sessionOn: () => 'full' };
}

function halvesOverlap(a: DaySession, b: DaySession): boolean {
  if (a === 'full' || b === 'full') return true;
  return a === b;
}

function sharedDays(a: DayCover, b: DayCover): string[] {
  const from = a.startDate > b.startDate ? a.startDate : b.startDate;
  const to = a.endDate < b.endDate ? a.endDate : b.endDate;
  if (from > to) return [];
  return eachDay(from, to).filter((d) => halvesOverlap(a.sessionOn(d), b.sessionOn(d)));
}

// ---------------------------------------------------------------------------
// Public checks
// ---------------------------------------------------------------------------

/** The days a trip and a leave request both claim (empty when they do not overlap). */
export function tripLeaveOverlapDates(trip: TripSpan, leave: LeaveCommitment): string[] {
  return sharedDays(tripCover(trip), leaveCover(leave));
}

/** The days two trips both claim — a trip returning in the morning and one leaving that afternoon share none. */
export function tripTripOverlapDates(a: TripSpan, b: TripSpan): string[] {
  return sharedDays(tripCover(a), tripCover(b));
}

/** The days a trip and an on-duty row both claim. */
export function tripOnDutyOverlapDates(trip: TripSpan, onDuty: OnDutyCommitment): string[] {
  return sharedDays(tripCover(trip), wholeDays(onDuty));
}

/** Every commitment the trip overlaps: leave first, then trips, then on-duty, each in input order. */
export function findTripOverlaps(trip: TripSpan, commitments: TripCommitments): TripOverlap[] {
  const found: TripOverlap[] = [];
  const push = (kind: TripOverlapKind, id: string, dates: string[]): void => {
    if (dates.length > 0) found.push({ kind, id, dates });
  };
  for (const row of commitments.leave ?? []) push('leave', row.id, tripLeaveOverlapDates(trip, row));
  for (const row of commitments.trips ?? []) push('trip', row.id, tripTripOverlapDates(trip, row));
  for (const row of commitments.onDuty ?? []) {
    push('on_duty', row.id, tripOnDutyOverlapDates(trip, row));
  }
  return found;
}

// ---------------------------------------------------------------------------
// Duplicates
// ---------------------------------------------------------------------------

/**
 * The one spelling of a city the module compares and stores (`TravelCityTier.city`): Unicode
 * NFKC, trimmed, inner whitespace collapsed, lower case. "  New  Delhi" and "new delhi" match.
 */
export function normalizeCityKey(city: string): string {
  return city.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
}

export interface DuplicateTripShape {
  /** Absent for the trip being created. */
  id?: string;
  personId: string;
  startDate: string;
  endDate: string;
  /** The trip's own destination field (free text, usually the city). */
  destination?: string | null;
  legs: readonly { fromCity: string; toCity: string }[];
  status?: TravelTripStatus;
}

/** Statuses that no longer hold the dates: a new request for the same journey is not a duplicate. */
const RELEASED: ReadonlySet<TravelTripStatus> = new Set([
  'rejected',
  'withdrawn',
  'cancelled',
  'expired',
]);

/**
 * Where a trip goes, as normalised city keys: the destination it names and the city its first leg
 * arrives in. A one-way Mumbai to Delhi and a round trip Mumbai to Delhi and back both go to
 * "delhi"; a return leg is never the destination.
 */
export function primaryDestinationKeys(
  trip: Pick<DuplicateTripShape, 'destination' | 'legs'>,
): Set<string> {
  const keys = new Set<string>();
  const named = trip.destination ? normalizeCityKey(trip.destination) : '';
  if (named) keys.add(named);
  const firstLeg = trip.legs[0];
  const firstArrival = firstLeg ? normalizeCityKey(firstLeg.toCity) : '';
  if (firstArrival) keys.add(firstArrival);
  return keys;
}

/**
 * Do two date ranges cover the "same days" for a duplicate: they overlap AND they start on the
 * same day, end on the same day, or one lies wholly inside the other. A staggered overlap (each
 * trip has days the other has not, e.g. 10-12 Oct and 12-14 Oct) is not a duplicate: it stays the
 * overlap WARNING (`findTripOverlaps`), since back-to-back trips are legitimate.
 */
function coversSameDays(
  a: { startDate: string; endDate: string },
  b: { startDate: string; endDate: string },
): boolean {
  if (a.startDate > b.endDate || b.startDate > a.endDate) return false;
  if (a.startDate === b.startDate || a.endDate === b.endDate) return true;
  const aInsideB = a.startDate >= b.startDate && a.endDate <= b.endDate;
  const bInsideA = b.startDate >= a.startDate && b.endDate <= a.endDate;
  return aInsideB || bInsideA;
}

/**
 * EC-2's "duplicate refused": the same person going to the same primary destination (city
 * normalised, `primaryDestinationKeys`) over the same days (`coversSameDays`). One-way against
 * round trip, a different origin or extra legs do not make it a different trip. A trip is never a
 * duplicate of itself, nor of one that was rejected, withdrawn, cancelled or expired.
 */
export function isDuplicateTrip(candidate: DuplicateTripShape, existing: DuplicateTripShape): boolean {
  if (candidate.id !== undefined && candidate.id === existing.id) return false;
  if (existing.status !== undefined && RELEASED.has(existing.status)) return false;
  if (candidate.personId !== existing.personId) return false;
  if (!coversSameDays(candidate, existing)) return false;
  const theirs = primaryDestinationKeys(existing);
  for (const key of primaryDestinationKeys(candidate)) {
    if (theirs.has(key)) return true;
  }
  return false;
}

/** The first of `existing` that `candidate` duplicates, or null. */
export function findDuplicateTrip<T extends DuplicateTripShape>(
  candidate: DuplicateTripShape,
  existing: readonly T[],
): T | null {
  return existing.find((row) => isDuplicateTrip(candidate, row)) ?? null;
}
